import type { CaseStudy } from '../types';

export const opec: CaseStudy = {
  id: 'opec',
  order: 14,
  title: 'OPEC 与石油配额',
  english: 'OPEC & the Oil Cartel',
  category: 'game',
  era: '1960 — 今',
  region: '全球 / 中东主导',
  accent: '#7c5c93',
  hook: '卡特尔最难的不是把价格抬起来，而是让每个成员相信：别人不会偷偷多卖一点。',
  question: '一群产油国明明有共同利益，为什么配额总是难以被严格遵守？',
  summary:
    'OPEC 通过协调产量影响油价，但每个成员都有“别人减产、自己增产”的短期激励。配额能否执行，取决于监测、惩罚、沙特等核心成员的剩余产能，以及外部替代供给和需求变化。',
  thesis: '卡特尔不是一份协议，而是一个持续执行的博弈；价格越高，作弊的诱惑通常越大。',
  quote: '共同利益让合作变得可能，个体利益让合作变得困难。',
  isNew: true,
  entryNodeIds: ['opec-cartel', 'opec-quota', 'opec-enforce'],
  focusNodeId: 'opec-enforce',
  lensIds: ['collective-action', 'commitment', 'scarcity', 'regulation'],
  opportunityIds: ['opp-energy-audit'],
  sourceIds: ['opec-history', 'eia-oil-prices'],
  facts: [
    {
      text: 'OPEC 于 1960 年在巴格达成立，创始成员包括伊朗、伊拉克、科威特、沙特阿拉伯和委内瑞拉。',
      sourceIds: ['opec-history'],
    },
    {
      text: '1973 年石油禁运与随后的减产使国际油价大幅上升；油价冲击的具体幅度取决于基准原油、名义/实际价格和时点。',
      sourceIds: ['opec-history', 'eia-oil-prices'],
    },
    {
      text: 'OPEC 的产量目标依赖成员国协商与执行；历史上多次出现超产、价格战与外部供给增加导致的协调困难。',
      sourceIds: ['opec-history', 'eia-oil-prices'],
    },
  ],
  nodes: [
    {
      id: 'opec',
      caseId: 'opec',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: 'OPEC',
      summary: '用协调产量影响油价的产油国集团。',
      detail:
        'OPEC 不是一个超国家政府，也没有全球执法权。它通过定期会议设定产量目标，由成员国自行执行。油价、财政需求、剩余产能和地缘政治让“共同减产”成为持续谈判，而不是一次性决定。',
      boundary: 'OPEC 对油价有影响力，但无法完全控制价格；美国页岩油、需求周期和地缘冲突同样重要。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['opec-history'],
    },
    {
      id: 'opec-cartel',
      caseId: 'opec',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '卡特尔：把竞争变成协调',
      summary: '成员共同限制产量，试图把价格维持在竞争水平之上。',
      detail:
        '如果每个产油国都独立增产，价格会下降；如果共同减产，成员整体收入可能上升。卡特尔的核心是将外部竞争内部化，用配额代替价格战。但这也意味着成员必须放弃一部分短期销量。',
      boundary: '卡特尔需要可观察的产量、稳定的成员结构和有限的外部替代供给。',
      counter: '非 OPEC 产油国不受配额约束，可能在高油价时增产，侵蚀 OPEC 的市场份额。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['opec-history'],
    },
    {
      id: 'opec-quota',
      caseId: 'opec',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '配额：共同减产，分配份额',
      summary: '每个成员拿到产量上限，但上限如何分配本身就是利益冲突。',
      detail:
        '配额需要决定谁减多少、谁保留多少市场份额。财政压力大的国家更希望多产；储量、产能和人口不同的国家也有不同诉求。配额分配不是技术问题，而是权力与利益分配问题。',
      boundary: '当需求旺盛、价格高时，配额更容易维持；需求下滑时，成员更想抢份额。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['opec-history'],
    },
    {
      id: 'opec-enforce',
      caseId: 'opec',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '执行：谁来看住谁',
      summary: '没有监测与惩罚，配额只是纸面承诺；超产者获得短期收益。',
      detail:
        '如果成员能悄悄增产而不被惩罚，合作会被逐利行为侵蚀。OPEC 依赖产量数据、部长级会议、沙特等核心成员的剩余产能和长期声誉来维持协调。执行不是一次性的，而是每轮价格与需求变化时重新谈判。',
      boundary: '监测能力、惩罚可信度和成员数量决定执行难度；外部供给和需求冲击会加大压力。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['opec-history'],
    },
    {
      id: 'opec-outside',
      caseId: 'opec',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '外部供给：高油价会召唤替代者',
      summary: '价格越高，非 OPEC 产量、替代能源和效率投资越有吸引力。',
      detail:
        '高油价会刺激页岩油、深海油田、天然气和新能源投资，也会促使消费国提高效率。替代供给需要时间，但一旦形成，就会削弱 OPEC 的定价能力。卡特尔越成功，越可能加速自己的替代。',
      boundary: '替代供给受投资周期、技术、资本成本和政策影响；短期供给弹性通常很低。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['eia-oil-prices'],
    },
    {
      id: 'opec-condition',
      caseId: 'opec',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：需求缺乏弹性与剩余产能',
      summary: '短期石油需求难以大幅替代，核心产油国又有闲置产能，协调才有力量。',
      detail:
        '石油在交通和化工中的短期替代性低，消费者对价格的反应有时滞；同时，沙特等国保有剩余产能，可以在需要时增产或减产来稳定市场。需求弹性和剩余产能是 OPEC 影响力的两个支点。',
      boundary: '能源转型、需求下滑或产油国财政危机，会削弱这两个支点。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['opec-history', 'eia-oil-prices'],
    },
    {
      id: 'opec-outcome',
      caseId: 'opec',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：价格影响力与持续摩擦',
      summary: 'OPEC 能在某些时期影响油价，但无法消除成员作弊与外部替代。',
      detail:
        '在供给紧张、成员协调较好时，OPEC 的产量决定可以显著影响价格；在需求下滑、成员财政压力大或外部供给增加时，配额更容易破裂。它的力量是周期性和条件性的，而不是永久的价格控制。',
      boundary: '油价同时受战争、制裁、库存、美元和需求预期影响；不能把每次油价变化都归因于 OPEC。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['opec-history', 'eia-oil-prices'],
    },
    {
      id: 'opec-risk',
      caseId: 'opec',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：作弊与囚徒困境',
      summary: '每个成员都希望别人减产、自己增产；合作越成功，作弊收益越高。',
      detail:
        '当价格因减产上升，成员的超产收益也上升。若惩罚不可信，合作会逐步瓦解。这不是道德问题，而是激励结构：个体理性行为可能导致集体更差的结果。',
      boundary: '长期重复互动、透明数据和可信惩罚可以把博弈从一次性转向合作。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['opec-history'],
    },
    {
      id: 'opec-opp',
      caseId: 'opec',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：为波动做“减震器”',
      summary: '油价波动越大，企业越需要能源审计、对冲、替代与需求响应服务。',
      detail:
        '能源成本上升时，节能改造、负荷管理、采购对冲和供应链替代的需求会增加。对普通创业者，机会不一定是预测油价，而是帮企业降低对油价的暴露：测量能耗、改造设备、重谈合同、设计备用方案。',
      boundary: '能源服务受资本开支、补贴和客户付费意愿影响；油价下跌时需求会迅速减弱。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['eia-oil-prices'],
    },
  ],
  edges: [
    {
      id: 'opec-e1',
      caseId: 'opec',
      from: 'opec',
      to: 'opec-cartel',
      label: '共同限制产量',
      explanation: '产油国试图用协调产量替代各自为战的竞争。',
      tone: 'cause',
    },
    {
      id: 'opec-e2',
      caseId: 'opec',
      from: 'opec',
      to: 'opec-quota',
      label: '把合作拆成份额',
      explanation: '共同目标必须落到每个国家具体生产多少，利益冲突随之出现。',
      tone: 'cause',
    },
    {
      id: 'opec-e3',
      caseId: 'opec',
      from: 'opec-cartel',
      to: 'opec-enforce',
      label: '合作需要执行',
      explanation: '没有监测与惩罚，成员可以通过超产获得短期收益。',
      tone: 'enable',
    },
    {
      id: 'opec-e4',
      caseId: 'opec',
      from: 'opec-quota',
      to: 'opec-enforce',
      label: '配额需要监督',
      explanation: '配额越具体，越需要数据、会议与惩罚机制来判断是否被遵守。',
      tone: 'cause',
    },
    {
      id: 'opec-e5',
      caseId: 'opec',
      from: 'opec-quota',
      to: 'opec-outcome',
      label: '供给收紧',
      explanation: '当成员共同减产，短期供给下降，价格获得上行压力。',
      tone: 'cause',
    },
    {
      id: 'opec-e6',
      caseId: 'opec',
      from: 'opec-enforce',
      to: 'opec-outcome',
      label: '执行决定可信度',
      explanation: '配额被遵守的程度，决定市场是否相信减产会真正发生。',
      tone: 'cause',
    },
    {
      id: 'opec-e7',
      caseId: 'opec',
      from: 'opec-outside',
      to: 'opec-outcome',
      label: '替代供给反击',
      explanation: '高油价刺激非 OPEC 产量与替代能源，削弱卡特尔定价能力。',
      tone: 'risk',
    },
    {
      id: 'opec-e8',
      caseId: 'opec',
      from: 'opec-condition',
      to: 'opec-outcome',
      label: '需求与产能前提',
      explanation: '短期需求缺乏弹性、核心成员拥有剩余产能，协调才更有力量。',
      tone: 'enable',
    },
    {
      id: 'opec-e9',
      caseId: 'opec',
      from: 'opec-outcome',
      to: 'opec-risk',
      label: '高价放大作弊诱惑',
      explanation: '价格越高，超产的短期收益越大，合作越难维持。',
      tone: 'risk',
    },
    {
      id: 'opec-e10',
      caseId: 'opec',
      from: 'opec-risk',
      to: 'opec-opp',
      label: '波动产生服务需求',
      explanation: '价格波动让企业愿意为能源效率、对冲和替代方案付费。',
      tone: 'enable',
    },
  ],
};
